import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import type { AdminSkill } from '@prepzio/shared';
import { useMemo, useState } from 'react';

import { SkillPicker } from '../components/skill-picker';
import {
  useAcceptSkill,
  useMergeSkill,
  usePromoteSkill,
  useSkillReviewQueue,
} from '../hooks/use-content';
import { useT } from '../i18n';
import type { SkillReviewQueueItem } from '../lib/api';
import { errorMessage } from '../lib/ui';

/**
 * BR-90 / PRE-123 — the skill merge queue.
 *
 * PRE-113 decided who waits. A skill a learner's CV named that our taxonomy did
 * not have is written on arrival and used at once; the learner never stops. The
 * queue is ours, and until this screen it had no way of being looked at, which
 * made "the admin waits" a polite way of saying nobody ever would.
 *
 * The screen is a decision list, not a data table. Every row is already live —
 * nothing here turns a skill on — so the question is never "should this exist"
 * but "where does it belong", and the three answers are the three buttons.
 */

/** Derive a kebab slug from a title, the same rule the skill manager uses. */
function slugify(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Merge: point this row at the one we keep.
 *
 * The consequence is spelled out on screen because it is the opposite of what
 * "merge" suggests in most tools. Nothing is deleted — a learner's CV said this
 * word, and removing the row would retroactively unsay it.
 */
function MergeModal({
  skill,
  excludeIds,
  onClose,
  onSubmit,
  busy,
  error,
}: {
  skill: SkillReviewQueueItem;
  /** Queued rows: never a valid merge target, so they are hidden from search. */
  excludeIds: readonly string[];
  onClose: () => void;
  onSubmit: (targetId: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const [targetId, setTargetId] = useState<string | null>(null);
  // PRE-155: held rather than looked up. The picker searches the server, so the
  // modal no longer has a list to find the target's title in — and the title is
  // the whole point of the sentence below.
  const [target, setTarget] = useState<AdminSkill | null>(null);
  const t = useT();
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={t('skillQueue.mergeTitle', { title: skill.title })}
    >
      <Stack gap="md">
        <SkillPicker
          label={t('skillQueue.mergeTarget')}
          placeholder={t('skillQueue.mergeTargetPlaceholder')}
          value={targetId}
          selected={target}
          excludeIds={excludeIds}
          onChange={setTargetId}
          onChangeSkill={setTarget}
        />
        {target && (
          <Alert color="blue" variant="light">
            <Text size="sm">
              {t('skillQueue.mergeExplain', { source: skill.title, target: target.title })}
            </Text>
            <Text size="sm" mt={4} c="dimmed">
              {t('skillQueue.mergeDetail', {
                users: skill.userCount,
                questions: skill.questionCount,
                target: target.title,
              })}
            </Text>
          </Alert>
        )}
        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!targetId || busy}
            onClick={() => {
              if (targetId) onSubmit(targetId);
            }}
          >
            {t('skillQueue.merge')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Promote: take it into the permanent taxonomy, correcting it on the way in. */
function PromoteModal({
  skill,
  onClose,
  onSubmit,
  busy,
  error,
}: {
  skill: SkillReviewQueueItem;
  onClose: () => void;
  onSubmit: (input: { title: string; slug: string; parentId: string | null }) => void;
  busy: boolean;
  error: string | null;
}) {
  // Prefilled with what arrived: most rows need no correction, and normalisation
  // already produced a usable slug.
  const [title, setTitle] = useState(skill.title);
  const [slug, setSlug] = useState(skill.slug);
  const [parentId, setParentId] = useState<string | null>(null);
  const t = useT();
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={t('skillQueue.promoteTitle', { title: skill.title })}
    >
      <Stack gap="md">
        <TextInput
          label={t('skillQueue.heading')}
          value={title}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setTitle(value);
            if (slug === slugify(title)) setSlug(slugify(value));
          }}
        />
        <TextInput
          label={t('skillQueue.slug')}
          description={t('skillQueue.slugHint')}
          value={slug}
          onChange={(e) => {
            setSlug(e.currentTarget.value);
          }}
        />
        {/*
         * The row cannot be its own parent — PRE-92 rejects that with a 409, and
         * offering it in the list only teaches that by way of an error message.
         * The merge modal has excluded its own row since PRE-123; this one was
         * the copy that missed it.
         */}
        <SkillPicker
          label={t('skillQueue.parent')}
          placeholder={t('skillQueue.parentPlaceholder')}
          clearable
          value={parentId}
          excludeIds={[skill.id]}
          onChange={setParentId}
        />
        <Text size="sm" c="dimmed">
          {t('skillQueue.promoteNote')}
        </Text>
        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!title.trim() || !slug.trim() || busy}
            onClick={() => {
              onSubmit({ title: title.trim(), slug: slug.trim(), parentId });
            }}
          >
            {t('skillQueue.promote')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function SkillQueueScreen() {
  const t = useT();
  const queue = useSkillReviewQueue();
  const merge = useMergeSkill();
  const promote = usePromoteSkill();
  const accept = useAcceptSkill();

  const [merging, setMerging] = useState<SkillReviewQueueItem | null>(null);
  const [promoting, setPromoting] = useState<SkillReviewQueueItem | null>(null);

  const items = queue.data?.items ?? [];
  // A queued row is never a valid merge target: it is itself waiting, and the
  // API refuses a target that redirects. Offering it would be a 409 with a
  // click in front of it.
  // PRE-155: ids rather than a filtered list — the merge picker searches the
  // server, so what it needs is what to leave OUT, not everything to put in.
  const queuedIds = useMemo(() => items.map((i) => i.id), [items]);

  const busy = merge.isPending || promote.isPending || accept.isPending;

  if (queue.isPending) return <Loader size="sm" />;
  if (queue.error) {
    return (
      <Alert color="red" variant="light">
        {errorMessage(queue.error)}
      </Alert>
    );
  }

  // The empty state says what empty MEANS. "No results" would be a lie here:
  // an empty queue is the taxonomy being current, which is the goal.
  if (items.length === 0) {
    return (
      <Card withBorder padding="lg">
        <Stack gap={6}>
          <Text fw={600}>{t('skillQueue.emptyTitle')}</Text>
          <Text size="sm" c="dimmed">
            {t('skillQueue.emptyBody')}
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      <Alert color="blue" variant="light">
        <Text size="sm">{t('skillQueue.banner')}</Text>
      </Alert>

      <Text c="dimmed" size="sm">
        {t('skillQueue.count', { count: items.length })}
      </Text>

      <Table.ScrollContainer minWidth={760}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('skillQueue.colLabel')}</Table.Th>
              <Table.Th w={110}>{t('skillQueue.colUsage')}</Table.Th>
              <Table.Th w={100}>{t('skillQueue.colQuestions')}</Table.Th>
              <Table.Th w={120}>{t('skillQueue.colArrived')}</Table.Th>
              <Table.Th w={300}>{t('skillQueue.colDecision')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td>
                  <Text fw={600} size="sm">
                    {item.title}
                  </Text>
                  <Text size="xs" c="dimmed" ff="monospace">
                    {item.slug}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={item.userCount > 1 ? 'teal' : 'gray'} size="sm">
                    {t('skillQueue.people', { count: item.userCount })}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{item.questionCount}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {item.createdAt.slice(0, 10)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Button
                      size="compact-xs"
                      variant="light"
                      disabled={busy}
                      onClick={() => {
                        setMerging(item);
                      }}
                    >
                      {t('skillQueue.merge')}
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="light"
                      color="teal"
                      disabled={busy}
                      onClick={() => {
                        setPromoting(item);
                      }}
                    >
                      {t('skillQueue.promote')}
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      disabled={busy}
                      onClick={() => {
                        accept.mutate({ id: item.id });
                      }}
                    >
                      {t('skillQueue.accept')}
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Text size="xs" c="dimmed">
        {t('skillQueue.acceptNote')}
      </Text>

      {merging && (
        <MergeModal
          skill={merging}
          excludeIds={queuedIds}
          busy={busy}
          error={merge.error ? errorMessage(merge.error) : null}
          onClose={() => {
            setMerging(null);
            merge.reset();
          }}
          onSubmit={(targetId) => {
            merge.mutate(
              { id: merging.id, targetId },
              {
                onSuccess: () => {
                  setMerging(null);
                },
              },
            );
          }}
        />
      )}

      {promoting && (
        <PromoteModal
          skill={promoting}
          busy={busy}
          error={promote.error ? errorMessage(promote.error) : null}
          onClose={() => {
            setPromoting(null);
            promote.reset();
          }}
          onSubmit={(input) => {
            promote.mutate(
              { id: promoting.id, ...input },
              {
                onSuccess: () => {
                  setPromoting(null);
                },
              },
            );
          }}
        />
      )}
    </Stack>
  );
}
